import React, {ComponentProps, useEffect, useState} from "react";
import {Button, Card, Col, Form, Input, message, Row, Space, Table} from "antd";
import {useForm} from "antd/lib/form/Form";
import {Link, useHistory} from "react-router-dom";
import * as api from "../../apis/platforms/admin/contract";
import StandardTable, {StandardColumnsType} from "../../components/StandardTable";
import Divider from "../../components/Divider";
import { ContractStatusMapping, ContractStatusOptions} from "../../utils/constants";
import dayjs from "dayjs";
import useAntTable from "../../utils/useAntTable";
import DictSelect from "../../components/DictSelect";
import PageHeaderLayout from "../../layout/pageHeaderLayout";
import withAuth from "../../utils/withAuth";
import UploadModal from "../../components/UploadModal";
import {useRequest} from "ahooks";
import useQueryParam from "../../utils/useQueryParam";
import useFormModal from "../../utils/useFormModal";
import DuplicateModal from "./duplicateModal";

const AddButton = withAuth('addButton', Button);
const ImportButton = withAuth('import', Button);
const ExportButton = withAuth('export', Button);
const ImportProductPrice = withAuth('importProductPrice', Button);
const DuplicateButton = withAuth('duplicate', Button);

function Contract(props: ComponentProps<any>) {
    const history = useHistory();
    const [importContractModalVisible, setImportContractModalVisible] = useState(false);
    const [importPriceModalVisible, setImportPriceModalVisible] = useState(false);
    const {tableProps, refresh, form, search, download, downloading} = useAntTable(api.getContracts, {
        method: 'post',
        downloadUrl: api.exportContracts
    })

    // useQueryParam(params => {
    //     const thisWeekNew = !!parseInt(params.thisWeekNew) ?? false;
    //     form.setFieldValue('thisWeekNew', thisWeekNew);
    // });

    useQueryParam((params) => {
        if (params.status != null) params.status = parseInt(params.status)
        if (params.thisWeekNew != null) params.thisWeekNew = parseInt(params.thisWeekNew)
        form.setFieldsValue(params)
        refresh()
    });

    const {loading: loadingDuplicate, runAsync: runDuplicate} = useRequest(api.duplicateContract,{
        manual: true,
    })

    const {modalProps, setVisible} = useFormModal({
        onRequest: runDuplicate,
        onOk: ()=> {
            setVisible(false);
            refresh();
        }
    })

    const {loading: loadingImportContract, run: runImportContract} = useRequest(api.importContract, {
        manual: true,
        onSuccess(res) {
            message.success('上传成功');
            setImportContractModalVisible(false);
            refresh();
        }
    });
    const {loading: loadingImportPrice, run: runImportPrice} = useRequest(api.importProductPrice, {
        manual: true,
        onSuccess(res) {
            message.success('上传成功');
            setImportPriceModalVisible(false);
        }
    });

    function handleDuplicate(record:any){
        setVisible(true, record);
    }

    let columns: StandardColumnsType = [
        {
            title: '合同号',
            key: 'code',
            dataIndex: 'code'
        },
        {
            title: '客户名称',
            key: 'dealerName',
            dataIndex: 'dealerName'
        },
        {
            title: '集团',
            key: 'groupName',
            dataIndex: 'groupName'
        },
        {
            title: '终端医院名称',
            key: 'hospitalName',
            dataIndex: 'hospitalName'
        },
        {
            title: '生效日期',
            key: 'startDate',
            dataIndex: 'startDate',
            render(v: any) {
                return !!v && dayjs(v)?.format('ll')
            }
        },
        {
            title: '状态',
            key: 'status',
            dataIndex: 'status',
            render(v: any, r: any) {
                //合同状态生效，但不在起止时间范围内，为失效
                // const nowStr = new Date().toISOString().substring(0,10);
                if (dayjs(Date.now()).isBefore(r.startDate,'day') || dayjs(Date.now()).isAfter(r.endDate,'day')) {
                    return '失效';
                }
                return ContractStatusMapping[v];
            }
        },
        {
            title: '操作',
            key: 'operation',
            dataIndex: 'operation',
            render(_, record: any) {
                return <Divider>
                    <Link to={`/contract/contract/detail/${record.id}`}>详情</Link>
                    <DuplicateButton type={'link'} onClick={()=>handleDuplicate(record)}>复制</DuplicateButton>
                </Divider>
            }
        }
    ];

    const searchColumns: StandardColumnsType = [
        {
            title: '客户名称',
            dataIndex: 'dealerName',
            key: 'dealerName',
            search: {
                colSpan: 8
            }
        },
        {
            title: '集团',
            dataIndex: 'groupName',
            key: 'groupName',
            search: {
                colSpan: 8
            }
        },
        {
            title: '合同号',
            dataIndex: 'code',
            key: 'code',
            search: {
                colSpan: 8
            }
        },
        {
            title: '终端医院',
            dataIndex: 'hospitalName',
            key: 'hospitalName',
            search: {
                colSpan: 8
            }
        },
        {
            title: '合同状态',
            dataIndex: 'status',
            key: 'status',
            search: {
                colSpan: 8,
                valueType: 'select',
                valueEnum: [...ContractStatusOptions, {value: -1, text: '失效'}]
            }
        },
        {
            title: '生效日期',
            dataIndex: 'startDate',
            key: 'startDate',
            search: {
                valueType: 'dateRange',
                colSpan: 8
            }
        },
        {
            title: '商品编码',
            dataIndex: 'productCode',
            key: 'productCode',
            search: {
                colSpan: 8
            }
        },
        {
            title: '商品名称',
            dataIndex: 'productName',
            key: 'productName',
            search: {
                colSpan: 8
            }
        },
        {
            title: '付款方式',
            dataIndex: 'paymentForm',
            key: 'paymentForm',
            search: {
                colSpan: 8,
                valueRender: <DictSelect dict={'PaymentForm'} allowClear nullLabel={'全部'}/>
            }
        },
        {
            title: '集团合同',
            dataIndex: 'groupContract',
            key: 'groupContract',
            search: {
                valueType: 'check',
                colSpan: 8
            }
        },
        {
            title: '含终端合同',
            dataIndex: 'terminalContract',
            key: 'terminalContract',
            search: {
                valueType: 'check',
                colSpan: 8
            }
        },
        {
            title: '同步云医购',
            dataIndex: 'synYunyigou',
            key: 'synYunyigou',
            search: {
                valueType: 'check',
                colSpan: 8
            }
        },
        {
            title: '本周新增',
            dataIndex: 'thisWeekNew',
            key: 'thisWeekNew',
            search:{
                valueType: 'check',
                colSpan: 8
            }
        }
    ];

    const toolbar = (
        <Space>
            <ImportProductPrice key={'importPrice'} type={"primary"}
                                onClick={() => setImportPriceModalVisible(true)}>更新价格</ImportProductPrice>
            <ExportButton key={'export'} type={'primary'} onClick={download} loading={downloading}>导出</ExportButton>
            <ImportButton key={'import'} type={'primary'}
                          onClick={() => setImportContractModalVisible(true)}>导入</ImportButton>
            <AddButton key={'add'} type={"primary"}
                       onClick={() => history.push(`/contract/contract/add/`)}>新增</AddButton>
        </Space>
    );

    return <PageHeaderLayout>
        <Form form={form}>
            <Form.Item noStyle name={'thisWeekNew'}/>
            <Form.Item noStyle name={'status'}/>
        </Form>
        <StandardTable {...tableProps} columns={columns} extSearchColumns={searchColumns} search={search} form={form}
                       toolbar={toolbar}/>
        <UploadModal key={'import-contract'} title={'导入合同'} template={'/template/合同导入模板.xlsx'}
                     confirmLoading={loadingImportContract}
                     onUpload={runImportContract} onCancel={() => setImportContractModalVisible(false)}
                     open={importContractModalVisible}/>
        <UploadModal key={'import-price'} title={'更新合同价格'} template={'/template/合同内商品价格批量更新模板.xlsx'}
                     confirmLoading={loadingImportPrice}
                     onUpload={runImportPrice} onCancel={() => setImportPriceModalVisible(false)}
                     open={importPriceModalVisible}/>
        <DuplicateModal {...modalProps} title={'复制合同'}/>
    </PageHeaderLayout>
}

export default Contract;
